import { useRef, useState } from "react";
import { clsx } from "clsx";
import { IconFileDescription, IconUpload } from "@tabler/icons-react";
import { Button } from "../button";
import { IconPill } from "../icon-pill";
import { Text } from "../typography";

export interface FileDropzoneCardProps {
  title: string;
  /** e.g. "PDF, JPG, JPEG, PNG - max 10MB" */
  supportedFormats?: string;
  helperText?: string;
  /** Extensions and/or MIME types. Files that don't match are rejected with an inline error. */
  accept?: string;
  /** Files larger than this are rejected with an inline error. Keep it in line with `supportedFormats`. */
  maxBytes?: number;
  disabled?: boolean;
  onFileSelect: (file: File) => void;
}

/** ".pdf,.jpg,.png" → ["PDF", "JPG", "PNG"]; "application/pdf" → ["PDF"]. */
function acceptLabels(accept: string): string[] {
  return accept
    .split(",")
    .map((entry) => entry.trim().replace(/^\./, "").split("/").pop() ?? "")
    .filter(Boolean)
    .map((label) => label.toUpperCase());
}

function matchesAccept(file: File, accept: string): boolean {
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept
    .split(",")
    .map((entry) => entry.trim().toLowerCase())
    .some((entry) =>
      entry.startsWith(".")
        ? name.endsWith(entry)
        : entry.endsWith("/*")
          ? type.startsWith(entry.slice(0, -1))
          : type === entry,
    );
}

function joinWithOr(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")}, or ${items[items.length - 1]}`;
}

function validateFile(file: File, accept: string, maxBytes?: number): string | null {
  if (accept && !matchesAccept(file, accept)) {
    return `Unsupported file format. Please upload a ${joinWithOr(acceptLabels(accept))} file.`;
  }
  if (maxBytes != null && file.size > maxBytes) {
    const limitMb = Math.round(maxBytes / (1024 * 1024));
    return `File is too large. The maximum size is ${limitMb} MB.`;
  }
  return null;
}

export function FileDropzoneCard({
  title,
  supportedFormats = "PDF, JPG, JPEG, PNG - max 10MB",
  helperText = "Supported: PDF, JPG, JPEG, PNG",
  accept = ".pdf,.jpg,.jpeg,.png",
  maxBytes,
  disabled = false,
  onFileSelect,
}: FileDropzoneCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so selecting the same file again re-triggers onChange
    e.target.value = "";
    if (!file) return;

    // The picker's `accept` is only a hint ("All files" bypasses it), so
    // enforce the advertised formats and size here.
    const problem = validateFile(file, accept, maxBytes);
    setError(problem);
    if (!problem) onFileSelect(file);
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <div
        className={clsx(
          "flex w-full items-center gap-3 rounded-2xl border bg-bgWhite p-6",
          // Phones: the button drops onto its own full-width row instead of
          // squeezing the title into a sliver.
          "max-md:flex-wrap max-md:p-4",
          error ? "border-accentError" : "border-borderDefault",
        )}
      >
        <IconPill size={48} tone="light">
          <IconFileDescription size={24} />
        </IconPill>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Text variant="bodyLg" tone="dark">
            {title}
          </Text>
          <Text variant="bodySm" tone="muted">
            {supportedFormats}
          </Text>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleChange}
          disabled={disabled}
        />
        <Button
          variant="primary"
          size="sm"
          leftIcon={<IconUpload size={18} />}
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="max-md:basis-full"
        >
          Upload file
        </Button>
      </div>
      {error ? (
        <span role="alert" className="px-1">
          <Text variant="bodySmBold" inline tone="error">
            {error}
          </Text>
        </span>
      ) : helperText ? (
        <Text variant="body" tone="body" className="px-1">
          {helperText}
        </Text>
      ) : null}
    </div>
  );
}
